import { clsx } from '@alexis/helpers/clsx';
import CheckboxItem from '@pages/FlightSearchResult/components/CheckboxItem';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { useSelector } from 'react-redux';

import { PAYMENT_METHOD_FILTER_BASED_ON_POS } from '@constants/flight';

import { convertFilterStateToDataForTracking } from '@helpers/flight';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getCurrentSiteState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type PaymentMethodsFilterProps = {
  className?: string;
  genzoTrackApplyFilterActionEvent: any;
  availablePaymentMethods: HomepageFlightsPaymentMethod[];
};

const PaymentMethodsFilter: React.FC<PaymentMethodsFilterProps> = ({
  className,
  genzoTrackApplyFilterActionEvent,
  availablePaymentMethods,
}) => {
  const { paymentMethods: paymentMethodsFromParams } = useFlightSearchPageParams();
  const { isPerLegSearch } = useFlightPLS();

  const currentSite = useSelector(getCurrentSiteState);

  const { activeLeg, paymentMethodIdsFilter, toggleMultiCitySingleFilter } =
    useFlightSearchFilters();

  const paymentMethodIdsFilterForActiveLeg =
    paymentMethodIdsFilter.find((item) => item.leg === activeLeg)?.values || [];

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const paymentMethods = availablePaymentMethods.filter(
    (paymentMethod) =>
      paymentMethodsFromParams.includes(paymentMethod.id) &&
      (
        PAYMENT_METHOD_FILTER_BASED_ON_POS[currentSite.countryCode] ||
        PAYMENT_METHOD_FILTER_BASED_ON_POS.default
      ).includes(paymentMethod.name.toLowerCase()),
  );

  const handlePaymentMethodClick = (paymentMethodId: number) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      searchParamKey: FlightSearchResultSearchParams.SelectedPaymentMethod,
      value: paymentMethodId,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      selected_payment_methods: convertFilterStateToDataForTracking(
        newFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });
  };

  if (!paymentMethods.length) return null;

  return (
    <div className={clsx(styles.categoryContent, className && styles.inDropdown)}>
      {paymentMethods.map((paymentMethod) => (
        <CheckboxItem
          className={className}
          key={paymentMethod.id}
          isChecked={paymentMethodIdsFilterForActiveLeg.includes(paymentMethod.id)}
          label={paymentMethod.name}
          onCheckboxToggle={() => handlePaymentMethodClick(paymentMethod.id)}
        />
      ))}
    </div>
  );
};

export default PaymentMethodsFilter;
